/**
 * Utility for parsing plain text into segments and mention objects.
 * Matches @mentions by longest displayName from userObjects; supports multi-word names
 * and excludes trailing punctuation from the highlight.
 */

/** User object for mention resolution (id + displayName only). */
export interface MentionUser {
  id: string;
  displayName: string;
}

/** Channel members API item shape (GET api/v1/channels/:channelId/members). */
export interface ChannelMemberApiItem {
  user_id: string;
  name: string;
  email_id?: string;
  avatar_url?: string | null;
  user_role?: string;
  membership_role?: string;
  company_id?: string;
  company_name?: string;
}

/** Hardcoded @Prism7 mention for Ask Prism7 right panel (single-word, no API lookup). */
export const PRISM7_MENTION_USER: MentionUser = {
  id: "prism7",
  displayName: "Prism7",
};

/**
 * Maps channel members API response (items with user_id, name) to MentionUser[] for parseMentionText.
 * Use the API: GET api/v1/channels/:channelId/members → response.items.
 */
export function mapChannelMembersApiToMentionUsers(
  items: ChannelMemberApiItem[]
): MentionUser[] {
  return (items || []).map((item) => ({
    id: item.user_id,
    displayName: item.name || "",
  }));
}

/**
 * Maps existing member list with id/name (e.g. from channelService) to MentionUser[].
 */
export function mapMembersToMentionUsers(
  members: Array<{ id: string; name: string }>
): MentionUser[] {
  return (members || []).map((m) => ({
    id: m.id,
    displayName: m.name || "",
  }));
}

/** A segment is either plain text or a mention object. */
export type MentionPart = string | { id: string; name: string };

/**
 * Parses text and returns an array of parts: strings (plain text) and mention objects.
 * Uses longest-match: tries to match the longest displayName at each '@' so multi-word
 * names (e.g. '@John von Neumann') are recognized. Trailing punctuation is not
 * included in the mention (e.g. '@John Doe.' → mention '@John Doe', '.' stays plain).
 *
 * @param text - Raw message text
 * @param userObjects - List of users with id and displayName (no hardcoded names)
 * @returns Array of string segments and { id, name: '@DisplayName' } objects
 */
export function parseMentionText(
  text: string,
  userObjects: MentionUser[]
): MentionPart[] {
  const parts: MentionPart[] = [];
  // Sort by displayName length descending so we try longest match first (e.g. "John von Neumann" before "John")
  const sortedUsers = [...userObjects].sort(
    (a, b) => b.displayName.length - a.displayName.length
  );
  let i = 0;

  while (i < text.length) {
    const atIndex = text.indexOf("@", i);
    if (atIndex === -1) {
      if (i < text.length) parts.push(text.slice(i));
      break;
    }
    // Push plain text before this '@'
    if (atIndex > i) parts.push(text.slice(i, atIndex));

    let matched = false;
    for (const user of sortedUsers) {
      const len = user.displayName.length;
      const segment = text.substring(atIndex + 1, atIndex + 1 + len);
      if (segment !== user.displayName) continue;
      // Require mention to end at word boundary (exclude trailing punctuation from highlight)
      const nextChar = text[atIndex + 1 + len];
      if (nextChar === undefined || !/\w/.test(nextChar)) {
        parts.push({ id: user.id, name: `@${user.displayName}` });
        i = atIndex + 1 + len;
        matched = true;
        break;
      }
    }
    if (!matched) {
      parts.push("@");
      i = atIndex + 1;
    }
  }

  return parts;
}
